import React from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  CheckCircle2,
  Loader2,
  Send,
  Zap,
  User,
  Mail,
  Lock,
  Phone,
  Briefcase,
} from 'lucide-react';

export const JobDetailExpressApplyDialog = ({
  open,
  onOpenChange,
  job,
  expressStep,
  setExpressStep,
  expressForm,
  setExpressForm,
  registering,
  handleExpressRegister,
  coverLetter,
  setCoverLetter,
  applying,
  handleExpressApply,
  onSignIn,
  onTrackApplication,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Zap className="w-4 h-4 text-green-600" />
            </div>
            <DialogTitle className="font-serif text-xl">SW Express Apply™</DialogTitle>
          </div>
          <DialogDescription>
            {expressStep === 1
              ? 'Create an account in 60 seconds to apply and track your applications.'
              : 'Almost done! Add an optional cover letter and submit.'}
          </DialogDescription>
        </DialogHeader>

        {expressStep === 1 ? (
          /* Step 1: Quick Registration */
          <form onSubmit={handleExpressRegister} className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <CheckCircle2 className="w-4 h-4 inline mr-2" />
              Apply to <strong>{job?.title}</strong> at {job?.company_name}
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="express-name" className="text-sm font-medium">
                  Full Name *
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="express-name"
                    type="text"
                    placeholder="Jane Smith"
                    value={expressForm.full_name}
                    onChange={(e) => setExpressForm({...expressForm, full_name: e.target.value})}
                    className="pl-10"
                    required
                    data-testid="express-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="express-email" className="text-sm font-medium">
                  Email *
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="express-email"
                    type="email"
                    placeholder="jane@example.com"
                    value={expressForm.email}
                    onChange={(e) => setExpressForm({...expressForm, email: e.target.value})}
                    className="pl-10"
                    required
                    data-testid="express-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="express-password" className="text-sm font-medium">
                  Password *
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="express-password"
                    type="password"
                    placeholder="Create a password"
                    value={expressForm.password}
                    onChange={(e) => setExpressForm({...expressForm, password: e.target.value})}
                    className="pl-10"
                    required
                    data-testid="express-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="express-phone" className="text-sm font-medium">
                  Phone (Optional)
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="express-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={expressForm.phone}
                    onChange={(e) => setExpressForm({...expressForm, phone: e.target.value})}
                    className="pl-10"
                    data-testid="express-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="express-license" className="text-sm font-medium">
                  License Type *
                </Label>
                <Select
                  value={expressForm.license_type}
                  onValueChange={(value) => setExpressForm({...expressForm, license_type: value})}
                >
                  <SelectTrigger className="mt-1" data-testid="express-license-select">
                    <SelectValue placeholder="Select your license" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LCSW">LCSW - Licensed Clinical Social Worker</SelectItem>
                    <SelectItem value="LMSW">LMSW - Licensed Master Social Worker</SelectItem>
                    <SelectItem value="LBSW">LBSW - Licensed Bachelor Social Worker</SelectItem>
                    <SelectItem value="SWA">SWA - Social Work Associate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={registering}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="express-register-btn"
              >
                {registering ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Continue to Apply
                  </>
                )}
              </Button>
            </DialogFooter>

            <p className="text-xs text-center text-gray-500 pt-2">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onSignIn}
                className="text-primary hover:underline"
              >
                Sign in
              </button>
            </p>
          </form>
        ) : expressStep === 2 ? (
          /* Step 2: Submit Application */
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Account Created!</span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                You can now track all your applications in your dashboard.
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">
                Cover Letter (Optional)
              </Label>
              <Textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Tell the employer why you're a great fit..."
                rows={4}
                className="mt-1 resize-none"
                data-testid="express-cover-letter"
              />
            </div>

            <DialogFooter>
              <Button
                onClick={handleExpressApply}
                disabled={applying}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="express-submit-btn"
              >
                {applying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : expressStep === 3 ? (
          /* Step 3: Success - Track Application */
          <div className="space-y-4 py-4">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Submitted!</h3>
              <p className="text-gray-600 mb-4">
                Your application for <strong>{job?.title}</strong> at {job?.company_name} has been sent.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">
                Track all your applications and get updates in your dashboard.
              </p>
              <Button
                onClick={onTrackApplication}
                className="bg-primary hover:bg-primary-hover text-white"
                data-testid="track-application-btn"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Track My Application
              </Button>
            </div>

            <DialogFooter className="pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  setExpressStep(1);
                }}
                className="w-full"
              >
                Continue Browsing Jobs
              </Button>
            </DialogFooter>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
